/* eslint-disable import/order -- the `react-semantic-ui-datepickers` stylesheet is a
   SIDE-EFFECT import, and the ordering this rule asks for moves module imports across
   it. Stylesheet order is cascade order, so that is a change to what the page renders,
   not to its types. */
import React, { useState, useEffect } from 'react';
import { Message, Table } from 'semantic-ui-react';
import 'react-semantic-ui-datepickers/dist/react-semantic-ui-datepickers.css';
import _ from 'lodash';
import useSWR from 'swr';

import { useAuthContext } from '../../../contexts/AuthContext';
import FHIRFetcher from '../../../data/FHIRFetcher';
import type { Fetched } from '../../../types/food';
import type { FlatProvenance } from '../../../types/flat-fhir';
import * as FHIRUtils from '../../../utils/FHIRUtils';
import { sortReducer } from '../../../utils/SortReducer';
import type { ResourceEntry } from '../../../utils/bundleNarrowing';
import { sortDirection } from '../../../utils/sortDirection';
import type { SortDirection } from '../../../utils/sortDirection';

import moment from 'moment';

type ActivityLogProps = {
  /**
   * Spliced into the `/Provenance` search. Optional because FHIR leaves
   * `Patient.id` `0..1`; an id-less member searches `patient=undefined`
   * rather than showing an error.
   */
  patientId: string | undefined;
  /** A switch to ACTIVITY_LOG revalidates the query. */
  activeTab?: string | undefined;
};

/**
 * The shape `sortReducer` maintains, stated at this call site because
 * `utils/SortReducer.ts` is unannotated — `useReducer` would otherwise infer
 * `any` for the state. Annotating the producer is a change with its own proof
 * obligation.
 */
type SortState = {
  column: string | null;
  data: FlatProvenance[];
  direction: SortDirection | null;
};

type SortAction =
  | { type: 'CHANGE_SORT'; column: string }
  | { type: 'CLEAR_FILTERS'; data: FlatProvenance[] };

function ActivityLog(props: ActivityLogProps) {
  const authInfo = useAuthContext();
  const [tableData, setTableData] = useState<FlatProvenance[]>([]);

  const [state, dispatch] = React.useReducer(sortReducer as React.Reducer<SortState, SortAction>, {
    column: null,
    data: tableData,
    direction: null,
  });
  const { column, data, direction } = state;

  const options = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    revalidateIfStale: false,
    revalidateOnReconnect: false,
  };

  //fetch provenance data

   
  const {
    data: provenanceData,
    error: provenanceError,
    mutate,
  } = useSWR<Fetched<fhir4.Bundle>, Error>(
    [`/Provenance/?patient=${props.patientId}&_sort=-_lastUpdated&_count=1000`, authInfo.token],
    FHIRFetcher<fhir4.Bundle>,
    options,
  );

  useEffect(() => {
    if (provenanceData && provenanceData.entry && provenanceData.entry.length) {
      setTableData([]);
      const tempTableData: FlatProvenance[] = [];
      // Cast, not `bundleEntriesOf`. That helper calls `Array.prototype.filter`, which drops
      // entries whose `resource` is absent or of another type. Upstream is a plain
      // `provenanceData.entry.forEach(...)` under the same enclosing guard. The cast emits nothing.
      (provenanceData.entry as ResourceEntry<'Provenance'>[]).forEach((provenance) => {
        const fhirProvenance = FHIRUtils.parseFHIRProvenance(provenance.resource);

         
        fhirProvenance.lastUpdated =
          fhirProvenance.lastUpdated &&
          moment(fhirProvenance.lastUpdated, 'YYYY-MM-DDTHH:mm:ss').format(
            'MMMM Do, YYYY HH:mm:ss',
          );
        setTableData((result) => {
          return [...result, fhirProvenance];
        });
        tempTableData.push(fhirProvenance);
      });
      dispatch({ type: 'CLEAR_FILTERS', data: tempTableData });
    }

     
  }, [provenanceData]);

  useEffect(() => {
    if (props.activeTab === 'ACTIVITY_LOG') {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; awaiting it would order this render behind a refetch.
      mutate(); //this will trigger SWR to re-Fetch
    }

     
  }, [props.activeTab]);
  return (
    <div>
      {provenanceData && !provenanceError && (
        <Table sortable celled padded color="olive">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                {...sortDirection(column === 'reason' ? direction : null)}
                onClick={() => dispatch({ type: 'CHANGE_SORT', column: 'reason' })}
              >
                Reason
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(column === 'activityCode' ? direction : null)}
                onClick={() => dispatch({ type: 'CHANGE_SORT', column: 'activityCode' })}
              >
                Activity Type
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(column === 'author' ? direction : null)}
                onClick={() => dispatch({ type: 'CHANGE_SORT', column: 'author' })}
              >
                Added By
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(column === 'lastUpdated' ? direction : null)}
                onClick={() => dispatch({ type: 'CHANGE_SORT', column: 'lastUpdated' })}
              >
                Date Added
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(column === 'target' ? direction : null)}
                onClick={() => dispatch({ type: 'CHANGE_SORT', column: 'target' })}
              >
                Target
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {data.map(
              ({ id, target, author, lastUpdated, activityCode, reason }: FlatProvenance) => (
                <Table.Row key={id}>
                  <Table.Cell>{reason}</Table.Cell>
                  <Table.Cell>{activityCode}</Table.Cell>
                  <Table.Cell>{author}</Table.Cell>
                  <Table.Cell>
                    {moment
                      .utc(lastUpdated, 'MMMM Do, YYYY hh:mm:ss')
                      .local()
                      .format('MMMM Do, YYYY hh:mm a')}
                  </Table.Cell>
                  <Table.Cell>{target}</Table.Cell>
                </Table.Row>
              ),
            )}
          </Table.Body>
        </Table>
      )}
      {provenanceData && !provenanceData.entry && (
        <Message warning header="No Activity Exists!!!" />
      )}
      {provenanceError && (
        <Message negative>
          <Message.Header>Oops!. Something went wrong.. </Message.Header>
          <p>{provenanceError.message}</p>
        </Message>
      )}
    </div>
  );
}

export default ActivityLog;
